import { t } from 'i18next';

const AGENT_CUSTOMERS = [
  { name: 'Claire Alice', top: 53.5, selected: true },
  { name: 'Fred Jones', top: 144.5, selected: false },
  { name: 'Oliver Smith', top: 232, selected: false },
] as const;

const CHART_GRID = [107, 140, 172.5, 205.5, 238.5] as const;

const CHART_AXIS = [
  { label: '€9K', y: 113 },
  { label: '€4.5K', y: 147 },
  { label: '€0', y: 178 },
  { label: '- €4.5K', y: 211 },
  { label: '- €9K', y: 243 },
] as const;

const CHART_TICKS = [123.5, 212.5, 302.5, 391.5] as const;

/** A tick inside a round badge, drawn from the badge's centre. */
function tickPath(cx: number, cy: number) {
  return `M${cx - 8} ${cy - 1}l6 6 12-12`;
}

export function SalesAgentsGraphic() {
  return (
    <svg viewBox="0 0 640 380" aria-hidden="true">
      <rect x="49.5" y="53.5" width="539" height="273" rx="11" className="fill-spark-surface" />
      <path
        d="M49.5 64.5a11 11 0 0 1 11-11h517a11 11 0 0 1 11 11v80h-539z"
        className="fill-illustration-primary/10"
      />
      <path
        d="M49.5 144.5h539M49.5 232h539"
        fill="none"
        strokeWidth="1.5"
        className="stroke-spark-border-default"
      />
      <rect
        x="49.5"
        y="53.5"
        width="539"
        height="273"
        rx="11"
        fill="none"
        strokeWidth="3"
        className="stroke-spark-border-default"
      />
      {AGENT_CUSTOMERS.map(({ name, top, selected }) => {
        const middle = top + 46.5;
        return (
          <g key={name}>
            {selected ? (
              <>
                <circle cx="93.5" cy={middle} r="22" className="fill-illustration-primary" />
                <path
                  d={tickPath(93.5, middle)}
                  fill="none"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="stroke-white"
                />
              </>
            ) : (
              <circle
                cx="94"
                cy={middle}
                r="20.5"
                strokeWidth="2.5"
                className="fill-spark-surface stroke-spark-border-default"
              />
            )}
            <text
              x="140"
              y={middle + 9.5}
              fontSize="27.5"
              fontWeight="500"
              className={selected ? 'fill-illustration-primary-text' : 'fill-spark-text-secondary'}
            >
              {name}
            </text>
            <text
              x="554"
              y={middle + 9.5}
              fontSize="27"
              textAnchor="end"
              className="fill-illustration-primary-text"
            >
              {t('components:addon_graphics.sales_agents.login')}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function QuotingGraphic() {
  return (
    <svg viewBox="0 0 495 294" aria-hidden="true">
      <rect
        x="100.5"
        y="44.5"
        width="293"
        height="64"
        rx="7"
        strokeWidth="3"
        className="fill-spark-surface stroke-spark-border-default"
      />
      <circle cx="141" cy="76" r="19" className="fill-spark-text-tertiary" />
      <text x="178" y="86.5" fontSize="23.5" fontWeight="600" className="fill-spark-text-secondary">
        {t('components:addon_graphics.quoting.request_quote')}
      </text>
      <path
        d="M247 125.5v44m-17.5-17.5 17.5 17.5 17.5-17.5"
        fill="none"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="stroke-emerald-500"
      />
      <rect
        x="100.5"
        y="186.5"
        width="293"
        height="64"
        rx="7"
        strokeWidth="3"
        className="fill-emerald-500/10 stroke-emerald-500"
      />
      <circle cx="137" cy="218" r="19" className="fill-emerald-500" />
      <path
        d={tickPath(137, 218)}
        fill="none"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="stroke-white"
      />
      <text x="174" y="228.5" fontSize="23.5" fontWeight="600" className="fill-emerald-600">
        {t('components:addon_graphics.quoting.order_completed')}
      </text>
    </svg>
  );
}

export function AnalyticsGraphic() {
  return (
    <svg viewBox="0 0 495 294" aria-hidden="true">
      <text x="42" y="55" fontSize="22" fontWeight="500" className="fill-spark-text-secondary">
        {t('components:addon_graphics.analytics.title')}
      </text>
      <rect x="42" y="72" width="141" height="11" rx="5.5" className="fill-spark-surface-strong" />
      <rect x="399" y="72" width="53" height="11" rx="5.5" className="fill-spark-text-tertiary" />
      <path
        d={CHART_GRID.map((y) => `M90 ${y}H452`).join('')}
        fill="none"
        strokeDasharray="4 2.6"
        className="stroke-spark-border-subtle"
      />
      <path
        d={CHART_TICKS.map((x) => `M${x} 239v5`).join('')}
        fill="none"
        className="stroke-spark-border-subtle"
      />
      <g fontSize="14.5" fontWeight="500" className="fill-spark-text-primary">
        {CHART_AXIS.map(({ label, y }) => (
          <text key={label} x="42" y={y}>
            {label}
          </text>
        ))}
      </g>
      <g fill="none" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        <path
          d="M93 120.5C93.5 119.9 95 118.3 96 117C97 115.7 98 113.7 99 112.5C100 111.3 100.7 110.2 102 110C103.3 109.8 105.7 108.3 107 111.3C108.3 114.4 109 123.8 110 128.5C111 133.1 112 135.2 113 139C114 142.7 115 148.1 116 150.9C117 153.8 117.3 155.7 119 156C120.7 156.2 124.3 154.2 126 152.5C127.7 150.8 128 148.4 129 145.6C130 142.7 131 138.3 132 135.4C133 132.6 134 131.4 135 128.5C136 125.6 137 120.7 138 118.1C139 115.4 140 113.4 141 112.5C142 111.6 143 110.6 144 112.5C145 114.4 146 118.4 147 124.1C148 129.8 149 141.2 150 146.4C151 151.7 152 153.1 153 155.4C154 157.7 155 159.2 156 160.5C157 161.8 158 162.3 159 163C160 163.7 161 163.9 162 164.5C163 165.1 164 165.8 165 166.5C166 167.1 166.7 167.7 168 168.4C169.3 169.1 171.7 169.5 173 170.6C174.3 171.6 175 173.3 176 174.4C177 175.6 178 176.3 179 177.5C180 178.8 180.7 180.8 182 182C183.3 183.2 185.7 183.8 187 184.5C188.3 185.2 189 186.2 190 186.5C191 186.8 192 186.6 193 186.6C194 186.6 195 186.6 196 186.6C197 186.6 197.7 186.8 199 186.5C200.3 186.1 202.7 184.8 204 184.4C205.3 184.1 206 184.2 207 184.4C208 184.7 208.8 185 210 186C211.2 187 212.8 189.3 214 190.4C215.2 191.5 216 191.3 217 192.6C218 193.9 219 197.1 220 198.4C221 199.8 222 199.6 223 200.6C224 201.5 225 203.3 226 204C227 204.7 227.5 205.1 229 204.5C230.5 204 233.5 202.2 235 200.5C236.5 198.8 237 196.4 238 194.5C239 192.6 240 191.1 241 189.1C242 187.1 243 184.3 244 182.5C245 180.7 246 179.8 247 178.4C248 177.1 249 175.5 250 174.5C251 173.5 252 172.8 253 172.5C254 172.2 255 172.4 256 172.5C257 172.6 258 172.4 259 173C260 173.6 261 175.3 262 176C263 176.7 264 176.3 265 177C266 177.7 267 179.3 268 180C269 180.7 270 180.6 271 181C272 181.4 272.3 182.3 274 182.5C275.7 182.8 279.3 182.9 281 182.6C282.7 182.2 283 181.3 284 180.5C285 179.7 285.7 178.7 287 178C288.3 177.3 290.7 176.1 292 176.5C293.3 176.9 294 178.9 295 180.5C296 182.1 297 184.7 298 186C299 187.3 299.7 187.5 301 188.5C302.3 189.5 304.7 191.3 306 192C307.3 192.7 308 192.5 309 192.5C310 192.6 311 192.9 312 192.5C313 192.2 314 192.5 315 190.4C316 188.3 317 182.9 318 180.1C319 177.2 320 175.7 321 173.5C322 171.2 323 168 324 166.5C325 165 325.7 165.1 327 164.5C328.3 163.9 330.7 163.1 332 163C333.3 162.9 334 163.7 335 164C336 164.3 337 164.3 338 164.5C339 164.7 339.7 164.8 341 165C342.3 165.2 344.7 165.8 346 166C347.3 166.2 348 166.4 349 166.5C350 166.5 350.7 166.2 352 166.5C353.3 166.9 355.5 167.8 357 168.5C358.5 169.2 359.5 170 361 171C362.5 172 364.7 173.8 366 174.4C367.3 175 368 174.4 369 174.5C370 174.6 371 174.9 372 175C373 175.1 374 175.1 375 175C376 174.9 377 174.6 378 174.5C379 174.4 379.3 174.8 381 174.5C382.7 174.1 386.3 172.9 388 172.5C389.7 172.1 389.8 172.3 391 172C392.2 171.7 393.2 171.7 395 170.5C396.8 169.3 400.3 165.9 402 165C403.7 164.1 403.7 164.8 405 165C406.3 165.2 408.7 166.2 410 166.5C411.3 166.8 412 166.6 413 166.6C414 166.6 415 166.9 416 166.5C417 166 418 165.3 419 164C420 162.7 421 159.9 422 158.5C423 157.2 424 157 425 156C426 155 427 153.2 428 152.6C429 151.9 430 152.3 431 152C432 151.7 432.7 151.1 434 150.5C435.3 149.9 437.3 149.1 439 148.6C440.7 148 442.7 147.3 444 147C445.3 146.7 446.5 146.5 447 146.5"
          className="stroke-amber-500"
        />
        <path
          d="M93 153C93.5 152.9 95 152.7 96 152.5C97 152.3 98 152.2 99 152C100 151.8 101 151.2 102 151C103 150.8 104 150 105 151C106 152 107 155.2 108 157.2C109 159.1 110 158.5 111 162.8C112 167 113 178.1 114 182.5C115 187 116 188.2 117 189.5C118 190.8 119 190.3 120 190.5C121 190.7 122 190.6 123 190.5C124 190.4 124.7 190.2 126 190C127.3 189.8 129.7 189.3 131 189C132.3 188.7 133 188.7 134 188.5C135 188.3 136 188.2 137 188C138 187.8 139 187.3 140 187C141 186.7 142 186.4 143 186C144 185.6 144.7 185.2 146 184.5C147.3 183.8 149.7 182.9 151 182C152.3 181.1 153 179.7 154 179C155 178.3 156 178.4 157 178C158 177.6 159 176.9 160 176.6C161 176.2 162 176.1 163 176C164 175.9 165 176 166 176C167 176 168 175.9 169 176C170 176.1 171 176.1 172 176.5C173 177 174 177.7 175 178.7C176 179.8 177 181.5 178 182.8C179 184.1 180 185.1 181 186.5C182 187.8 183 189.4 184 190.9C185 192.3 186 193.9 187 195.2C188 196.6 189 198.2 190 199C191 199.8 192 199.8 193 200C194 200.2 195 200 196 200C197 200 198 200.2 199 200C200 199.8 201 198.9 202 198.5C203 198.1 204 198.2 205 197.5C206 196.9 207 195.9 208 194.6C209 193.2 210 191.1 211 189.4C212 187.7 213 185.6 214 184.5C215 183.4 216 183 217 183C218 183 219 183.7 220 184.5C221 185.2 222 186.5 223 187.5C224 188.5 225 189.7 226 190.5C227 191.3 228 191.8 229 192.5C230 193.2 231 194 232 194.5C233 195 233.7 195.1 235 195.5C236.3 195.9 238.7 196.7 240 197C241.3 197.3 242 197 243 197C244 197 245 197.1 246 197C247 196.9 248 196.8 249 196.5C250 196.2 251 195.8 252 195.4C253 195.1 254 194.9 255 194.5C256 194.1 257 193.5 258 193C259 192.5 260 191.9 261 191.5C262 191.1 263 190.4 264 190.5C265 190.6 266 191.1 267 192.1C268 193.1 269 195.2 270 196.5C271 197.8 272 198.7 273 199.8C274 200.9 275 202.1 276 203C277 203.8 278 204.4 279 205C280 205.6 281 206.2 282 206.5C283 206.8 284 206.5 285 206.5C286 206.5 287 206.5 288 206.5C289 206.5 290 206.5 291 206.5C292 206.5 293 206.5 294 206.4C295 206.3 296 206.2 297 206C298 205.8 299 205.2 300 205C301 204.8 302 204.6 303 204.5C304 204.4 305 204.7 306 204.4C307 204.1 308 203.1 309 202.6C310 202.1 311 201.8 312 201.4C313 201 314 200.9 315 200C316 199.1 317 197.9 318 196.1C319 194.3 320 191.6 321 189.1C322 186.6 323 182.8 324 181.3C325 179.8 326 180.1 327 180C328 179.9 329 180.1 330 180.5C331 180.9 332 181.8 333 182.5C334 183.1 335 184 336 184.4C337 184.8 338 185 339 185C340 185 341 184.9 342 184.7C343 184.5 344 184.1 345 184C346 183.9 347 184 348 184C349 184 350 183.9 351 184C352 184.1 353 184.2 354 184.3C355 184.5 356 184.6 357 185C358 185.4 359 186.2 360 186.5C361 186.8 362 186.9 363 187C364 187.1 365 187 366 187C367 187 368 187 369 187C370 187 371 187.1 372 187C373 186.9 374 186.8 375 186.5C376 186.2 377 185.6 378 185C379 184.4 380 183.8 381 182.8C382 181.8 383 179.8 384 179C385 178.2 386 178.3 387 178C388 177.7 389 176.9 390 177C391 177.1 392 177.8 393 178.8C394 179.8 395 181.9 396 183.2C397 184.6 398 185.6 399 187C400 188.5 401 190.8 402 192C403 193.2 404 193.7 405 194.5C406 195.3 407 195.9 408 196.6C409 197.2 410 198.1 411 198.4C412 198.7 413 198.6 414 198.5C415 198.4 416 198.5 417 198C418 197.5 419 196 420 195.4C421 194.7 421.7 194.4 423 194C424.3 193.6 426.7 192.7 428 193C429.3 193.3 430 194.1 431 196.1C432 198.1 433 203 434 205.1C435 207.3 436 208.1 437 209C438 209.9 439 210.4 440 210.6C441 210.8 442 210.8 443 210.5C444 210.1 445 209.4 446 208.5C447 207.6 448.2 205.8 449 204.8C449.8 203.8 450.7 202.9 451 202.5"
          className="stroke-illustration-primary"
        />
      </g>
      <rect x="222" y="258" width="27" height="10" rx="5" className="fill-illustration-primary" />
      <rect x="259" y="258" width="28" height="10" rx="5" className="fill-amber-500" />
    </svg>
  );
}
